// Mobx
import TextAreaInput from '@straico-ui/forms/TextAreaInput';
import { inject, observer } from 'mobx-react';
// RHF
import { useFormContext } from 'react-hook-form';
// Components
import useThemeStore from 'Theme/store';
import {
  ChevronDownIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Menu, MenuItem, Tooltip, Badge } from '@mui/material';
import useChatStore from 'Components/Chat/store';
import { useState, useEffect } from 'react';
import get from 'lodash/get';
import { icons as modelIcons } from 'Theme/icons/modelsIcons';
import ModelsModal from 'Features/ModelsModal/index.js';

const SectionGeneral = ({ models, store }) => {
  const { register, setValue, watch } = useFormContext();
  const { theme } = useThemeStore();
  const {
    selectedModels: globalSelectedModels,
    selectedCombo: globalSelectedCombo,
    combos,
    combosLoading,
  } = useChatStore();

  const [localSelectedModels, setLocalSelectedModels] = useState([]);
  const [localSelectedCombo, setLocalSelectedCombo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);

  useEffect(() => {
    const selectedIds = watch('multi_default_models');
    if (selectedIds?.length && models?.length) {
      const selected = selectedIds
        .map((id) => models.find((m) => m._id === id))
        .filter(Boolean);
      setLocalSelectedModels(selected);
    }
  }, []);

  useEffect(() => {
    const selectedIds = localSelectedModels
      .map((m) => m._id)
      .sort()
      .join(',');

    const matchedCombo = combos.find((combo) => {
      const comboIds = combo.llms.slice().sort().join(',');
      return selectedIds === comboIds;
    });

    if (matchedCombo) {
      setLocalSelectedCombo(matchedCombo);
    } else if (localSelectedModels.length > 0) {
      setLocalSelectedCombo({ name: 'Selected models' });
    } else {
      setLocalSelectedCombo(null);
    }
  }, [localSelectedModels, combos]);

  useEffect(() => {
    setValue(
      'multi_default_models',
      localSelectedModels.map((m) => m._id)
    );
  }, [localSelectedModels, setValue]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleComboClick = (combo) => {
    const selected = combo.llms
      .map((id) => models.find((m) => m._id === id))
      .filter(Boolean);
    setLocalSelectedModels(selected);
    setLocalSelectedCombo(combo);
    handleMenuClose();
  };

  const handleRemoveModel = (idToRemove) => {
    const updatedModels = localSelectedModels.filter(
      (model) => model._id !== idToRemove
    );
    setLocalSelectedModels(updatedModels);
  };

  const getIcon = (icon) => {
    const Icon = get(modelIcons, icon);
    return Icon ? <Icon className="h-6 w-6" /> : null;
  };

  return (
    <div className="flex flex-col font-figtree">
      <p className="text-xl font-bold">Step 1: General adjustments</p>
      <div className="flex flex-col gap-4 pt-4">
        {/* Template Name Section */}
        <div className="flex flex-col gap-2">
          <label className="uppercase text-sm font-bold">Template name *</label>
          <TextAreaInput
            name="name"
            className={`w-full border ${
              theme === 'dark' ? 'dark:bg-lead' : 'bg-seasalt'
            } border-platinum border-solid text-sm p-2 rounded-md`}
            placeholder="Name of your prompt"
            {...register('name')}
            rows={2}
          />
        </div>

        {/* Description Section */}
        <div className="flex flex-col gap-2">
          <label className="uppercase text-sm font-bold">Description *</label>
          <TextAreaInput
            name="description"
            className={`w-full border ${
              theme === 'dark' ? 'dark:bg-lead' : 'bg-seasalt'
            } border-platinum border-solid text-sm p-2 rounded-md`}
            placeholder="A description for your prompt"
            {...register('description')}
            rows={3}
          />
        </div>

        {/* Default Models Section */}
        <div className="flex flex-col gap-2">
          <label className="uppercase text-sm font-bold">
            Default models *
          </label>
          <p className="text-xs py-2 text-pretty">
            Need assistance selecting LLMs for your template?{' '}
            <a
              className="text-violet-blue font-bold"
              target="_blank"
              href="https://straico.com/multimodel"
              rel="noreferrer"
            >
              Click here
            </a>{' '}
            to view related documentation.
          </p>

          <div
            className={`w-full border-2 ${
              theme === 'dark' ? 'dark:bg-white' : 'bg-white'
            } border-platinum text-sm p-2 rounded-[10px] flex flex-col gap-2`}
          >
            {/* Combo Selector */}
            <div className="flex items-center gap-2">
              <div className="flex-grow">
                {combosLoading ? (
                  <p>Loading combos...</p>
                ) : combos.length === 0 ? (
                  <p>No combos available</p>
                ) : (
                  <div
                    onClick={handleMenuOpen}
                    className={`cursor-pointer rounded-[10px] p-2 flex items-center justify-between ${
                      theme === 'dark'
                        ? 'bg-lead border-sonic-silver'
                        : 'bg-seasalt border-platinum'
                    } border-[1.5px]`}
                  >
                    <span className="text-sm">
                      {localSelectedCombo?.name || ''}
                    </span>

                    <Tooltip title="Select a combo" arrow placement="left">
                      <div className="ml-2">
                        <ChevronDownIcon className="h-5 w-5 text-violet-blue dark:text-tropical-indigo" />
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>

              <Tooltip title="Choose models" arrow placement="right">
                <div
                  onClick={() => setIsModelDialogOpen(true)}
                  className="w-[32px] h-[32px] bg-lavender hover:bg-ghost-white cursor-pointer rounded-[10px] dark:bg-ship-grey dark:hover:bg-quicksilver flex justify-center items-center"
                >
                  <PencilSquareIcon className="w-[18px] h-[18px] text-violet-blue dark:text-tropical-indigo" />
                </div>
              </Tooltip>
            </div>

            {/* Combo Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                style: {
                  maxHeight: 200, // altura máxima antes de hacer scroll
                  overflowY: 'auto', // scroll vertical si se excede
                  width: anchorEl ? anchorEl.clientWidth : 'auto',
                  backgroundColor: theme === 'dark' ? '#252425' : '#FBFBFA',
                  border: '1.5px solid #9091A461',
                  borderRadius: '10px',
                  marginTop: '6px',
                  boxShadow: 'none',
                },
                elevation: 0,
              }}
            >
              {combos
                .slice()
                .sort((a, b) => a.position - b.position)
                .map((combo) => (
                  <MenuItem
                    key={combo._id}
                    onClick={() => {
                      handleComboClick(combo);
                      handleMenuClose();
                    }}
                    sx={{
                      fontSize: '0.875rem',
                      color: theme === 'dark' ? '#FBFBFA' : '#3A3A3A',
                      '&:hover': {
                        backgroundColor:
                          theme === 'dark' ? '#373637' : '#EFEFEF',
                      },
                    }}
                  >
                    {combo.name}
                  </MenuItem>
                ))}
            </Menu>

            {/* Selected Models */}
            {localSelectedModels.map((model, index) => (
              <div className="relative group" key={index}>
                <div className="flex items-center gap-2">
                  {getIcon(model.icon)}
                  <span className="text-sm text-agate-violet dark:text-palladium">
                    {model.abbreviation}
                  </span>
                </div>

                {localSelectedModels.length > 1 && (
                  <div
                    className="hidden group-hover:block absolute z-50 p-[1px] rounded cursor-pointer select-none -left-1 -bottom-1 bg-lavender dark:bg-ship-grey text-violet-blue dark:text-stargate-shimmer"
                    onClick={() => handleRemoveModel(model._id)}
                  >
                    <XMarkIcon className="w-[10px] h-[10px] stroke-[3px]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Models Modal */}
      {isModelDialogOpen && (
        <ModelsModal
          open={isModelDialogOpen}
          onClose={() => setIsModelDialogOpen(false)}
          store={store}
          selectedModels={localSelectedModels}
          setSelectedModels={(models) => {
            setLocalSelectedModels(models);
            setValue(
              'multi_default_models',
              models.map((m) => m._id)
            );
          }}
          nocapabilities={true}
          themeValue={theme}
        />
      )}
    </div>
  );
};

export default inject('store')(observer(SectionGeneral));
